/**
 * ICSC Professional Competency Accreditation Framework (PCAF) Hooks
 *
 * React hooks for professional competency courses, enrollments, assessments, exams, and certificates
 */
import { useState, useEffect, useCallback } from 'react';
import * as competencyService from '../services/competencyService';

// ============================================================================
// COURSES & FRAMEWORKS
// ============================================================================

export const useCompetencyCourses = (status = 'published') => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await competencyService.getAllCourses(status);
      setCourses(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  return { courses, loading, error, refetch: fetchCourses };
};

export const useCompetencyCourse = (courseId) => {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCourse = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await competencyService.getCourseDetail(courseId);
      setCourse(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  return { course, loading, error, refetch: fetchCourse };
};

export const useCompetencyFrameworks = () => {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFrameworks = async () => {
      try {
        const data = await competencyService.getAllFrameworks();
        setFrameworks(data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load frameworks');
      } finally {
        setLoading(false);
      }
    };

    fetchFrameworks();
  }, []);

  return { frameworks, loading, error };
};

// ============================================================================
// ENROLLMENTS
// ============================================================================

export const useMyEnrollments = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEnrollments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await competencyService.getMyEnrollments();
      setEnrollments(data);
    } catch (err) {
      setError(err.response?.data?.detail  || 'Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  const enroll = useCallback(async (enrollmentData) => {
    try {
      const newEnrollment = await competencyService.enrollLearner(enrollmentData);
      await fetchEnrollments(); // Refresh list
      return newEnrollment;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to enroll');
    }
  }, [fetchEnrollments]);

  return { enrollments, loading, error, enroll, refetch: fetchEnrollments };
};

export const useEnrollmentProgress = (enrollmentId) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProgress = useCallback(async () => {
    if (!enrollmentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await competencyService.getProgress(enrollmentId);
      setProgress(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const markModuleComplete = useCallback(async (moduleId) => {
    try {
      await competencyService.updateProgress(enrollmentId, moduleId);
      await fetchProgress(); // Refresh progress
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to update progress');
    }
  }, [enrollmentId, fetchProgress]);

  return { progress, loading, error, markModuleComplete, refetch: fetchProgress };
};

// ============================================================================
// ASSESSMENTS
// ============================================================================

export const useCompetencyAssessment = (enrollmentId) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submitAssessment = useCallback(async (assessmentData) => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await competencyService.submitAssessment({
        enrollment_id: enrollmentId,
        ...assessmentData
      });
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit assessment');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [enrollmentId]);

  return { submitAssessment, submitting, error };
};

// ============================================================================
// EXAMS
// ============================================================================

export const useCompetencyExam = (enrollmentId) => {
  const [exam, setExam] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const startExam = useCallback(async () => {
    setError(null);
    try {
      const examData = await competencyService.startExam(enrollmentId);
      setExam(examData);
      return examData;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start exam');
      throw err;
    }
  }, [enrollmentId]);

  const submitExam = useCallback(async (examId, answers) => {
    setSubmitting(true);
    setError(null);
    try {
      const results = await competencyService.submitExam(examId, answers);
      return results;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit exam');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const getResults = useCallback(async (examId) => {
    try {
      const results = await competencyService.getExamResults(examId);
      return results;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get exam results');
      throw err;
    }
  }, []);

  return { exam, startExam, submitExam, getResults, submitting, error };
};

// ============================================================================
// EVIDENCE
// ============================================================================

export const useCompetencyEvidence = (enrollmentId) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submitEvidence = useCallback(async (evidenceData) => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await competencyService.submitEvidence({
        enrollment_id: enrollmentId,
        ...evidenceData
      });
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit evidence');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [enrollmentId]);

  return { submitEvidence, submitting, error };
};

// ============================================================================
// ACCREDITATION
// ============================================================================

export const useCompetencyAccreditation = (userId) => {
  const [accreditations, setAccreditations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAccreditations = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await competencyService.getUserAccreditations(userId);
      setAccreditations(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load accreditations');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAccreditations();
  }, [fetchAccreditations]);

  const submitForAccreditation = useCallback(async (enrollmentId) => {
    try {
      const result = await competencyService.submitForAccreditation(enrollmentId);
      await fetchAccreditations(); // Refresh list
      return result;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to submit for accreditation');
    }
  }, [fetchAccreditations]);

  return { accreditations, loading, error, submitForAccreditation, refetch: fetchAccreditations };
};

// ============================================================================
// CERTIFICATES
// ============================================================================

export const useCompetencyCertificates = () => {
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState(null);

  const issueCertificate = useCallback(async (certificateData) => {
    setIssuing(true);
    setError(null);
    try {
      const result = await competencyService.issueCertificate(certificateData);
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to issue certificate');
      throw err;
    } finally {
      setIssuing(false);
    }
  }, []);

  const verifyCertificate = useCallback(async (certificateNumber) => {
    try {
      const result = await competencyService.verifyCertificate(certificateNumber);
      return result;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to verify certificate');
    }
  }, []);

  return { issueCertificate, verifyCertificate, issuing, error };
};

// ============================================================================
// DASHBOARD
// ============================================================================

export const useCompetencyDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await competencyService.getDashboardStats();
        setStats(data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load dashboard stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, loading, error };
};
